import { useCallback, useEffect, useMemo, useRef } from 'react'
import constant from 'lodash/constant'
import noop from 'lodash/noop'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { bindWizardSteps, type WizardStepModule } from '@klear/ui/wizard/flow'
import { WizardFlowContent } from '@klear/ui/wizard/flow-content'
import { useReportedWizardFlow } from '@klear/ui/wizard/use-step-group-nav'
import type { ReceivableStepCtx } from '@/app/receivable/workflow/ReceivableWizardContext'
import { STEP_ID, STEP_LABELS } from '@/app/receivable/workflow/receivable-steps'
import {
  initialReceivableStepId,
  isReceivableHostStepSatisfied,
  type ReceivableWizardApi
} from '@/app/receivable/workflow/useReceivableWizard'
import SelectOrderStep from '@/app/receivable/workflow/steps/SelectOrderStep'
import PurchaseOrderStep from '@/app/receivable/workflow/steps/PurchaseOrderStep'
import InvoiceStep from '@/app/receivable/workflow/steps/InvoiceStep'
import ProofOfAcceptanceStep from '@/app/receivable/workflow/steps/ProofOfAcceptanceStep'
import ReviewStep from '@/app/receivable/workflow/steps/ReviewStep'

const selectOrderModule: WizardStepModule<ReceivableStepCtx> = {
  id: STEP_ID.SELECT_ORDER,
  label: STEP_LABELS[STEP_ID.SELECT_ORDER],
  isComplete: (ctx) => isReceivableHostStepSatisfied(STEP_ID.SELECT_ORDER, ctx.getForm()),
  component: SelectOrderStep
}

const purchaseOrderModule: WizardStepModule<ReceivableStepCtx> = {
  id: STEP_ID.PURCHASE_ORDER,
  label: STEP_LABELS[STEP_ID.PURCHASE_ORDER],
  isComplete: (ctx) => isReceivableHostStepSatisfied(STEP_ID.PURCHASE_ORDER, ctx.getForm()),
  component: PurchaseOrderStep
}

const invoiceModule: WizardStepModule<ReceivableStepCtx> = {
  id: STEP_ID.INVOICE,
  label: STEP_LABELS[STEP_ID.INVOICE],
  isComplete: (ctx) => isReceivableHostStepSatisfied(STEP_ID.INVOICE, ctx.getForm()),
  component: InvoiceStep
}

const proofModule: WizardStepModule<ReceivableStepCtx> = {
  id: STEP_ID.PROOF_OF_ACCEPTANCE,
  label: STEP_LABELS[STEP_ID.PROOF_OF_ACCEPTANCE],
  isComplete: (ctx) => isReceivableHostStepSatisfied(STEP_ID.PROOF_OF_ACCEPTANCE, ctx.getForm()),
  component: ProofOfAcceptanceStep
}

const reviewModule: WizardStepModule<ReceivableStepCtx> = {
  id: STEP_ID.REVIEW,
  label: STEP_LABELS[STEP_ID.REVIEW],
  isComplete: constant(false),
  component: ReviewStep
}

const RECEIVABLE_STEP_MODULES: Array<WizardStepModule<ReceivableStepCtx>> = [
  selectOrderModule,
  purchaseOrderModule,
  invoiceModule,
  proofModule,
  reviewModule
]

export type ReceivableFlowProps = {
  workflow: ReceivableWizardApi
  orderIdSeed?: number | null
  onNavStateChange: (nav: WizardStepGroupNavState) => void
}

export function ReceivableFlow({ workflow, orderIdSeed, onNavStateChange }: ReceivableFlowProps) {
  const goNextRef = useRef<() => void>(noop)
  const goPrevRef = useRef<() => void>(noop)

  const patchFormAndGoNext = useCallback(
    (recipe: Parameters<ReceivableWizardApi['patchForm']>[0]) => {
      workflow.patchForm(recipe)
      goNextRef.current()
    },
    [workflow.patchForm]
  )

  const attachExistingPurchaseOrder = useCallback(
    (purchaseOrderEventId: number) => {
      workflow.attachExistingPurchaseOrder(purchaseOrderEventId)
      goNextRef.current()
    },
    [workflow.attachExistingPurchaseOrder]
  )

  const goPrev = useCallback(() => {
    goPrevRef.current()
  }, [])

  const onProofSaved = useCallback(() => {
    workflow.markProofAdded()
    goNextRef.current()
  }, [workflow.markProofAdded])

  const onProofSkipped = useCallback(() => {
    goNextRef.current()
  }, [])

  const stepCtx = useMemo<ReceivableStepCtx>(
    () => ({
      form: workflow.form,
      getForm: workflow.getForm,
      patchForm: workflow.patchForm,
      patchFormAndGoNext,
      goPrev,
      proofAdded: workflow.proofAdded,
      attachExistingPurchaseOrder,
      onProofSaved,
      onProofSkipped,
      handleDone: workflow.handleDone
    }),
    [
      workflow.form,
      workflow.getForm,
      workflow.patchForm,
      workflow.proofAdded,
      workflow.handleDone,
      patchFormAndGoNext,
      goPrev,
      attachExistingPurchaseOrder,
      onProofSaved,
      onProofSkipped
    ]
  )

  const stepDefs = useMemo(() => bindWizardSteps(RECEIVABLE_STEP_MODULES, stepCtx), [stepCtx])

  const flow = useReportedWizardFlow(stepDefs, {
    initialStepId: initialReceivableStepId(
      {
        ...workflow.form,
        orderId: orderIdSeed ?? workflow.form.orderId
      },
      workflow.hashStepSeed
    ),
    stepSelection: 'gated',
    onNavStateChange
  })

  goNextRef.current = () => {
    void flow.goNext()
  }
  goPrevRef.current = flow.goPrev

  const syncUrlRef = useRef(workflow.syncUrl)
  syncUrlRef.current = workflow.syncUrl
  useEffect(() => {
    syncUrlRef.current(flow.activeStepId)
  }, [flow.activeStepId])

  return <WizardFlowContent flow={flow} showNav={false} className="contents" />
}
